import { useState, useEffect, useCallback } from "react";
import { Select, Radio, DatePicker, Switch, Checkbox } from "antd";
import "./filters.scss";
import WellBeEMPIService from "@services/empiService.service";
import moment from "moment";
import momentTZ from "moment-timezone";
import { useDataQualityReportStore } from "@stores/dataQualityReport";
import { cloneDeep, isEmpty, size } from "lodash";
const { Option } = Select;

interface FilterProps {
  view: string;
  onViewChange: (view: string) => void;
  disableDateRange?: boolean;
}

const { RangePicker } = DatePicker;

const Filters: React.FC<FilterProps> = ({ view, onViewChange, disableDateRange }) => {
  // importing states from the store
  const payer = useDataQualityReportStore((state) => state.payer);
  const setPayer = useDataQualityReportStore((state) => state.setPayer);
  const fileType = useDataQualityReportStore((state) => state.fileType);
  const setFileType = useDataQualityReportStore((state) => state.setFileType);
  const selectedDateRange = useDataQualityReportStore((state) => state.selectedDateRange);
  const setSelectedDateRange = useDataQualityReportStore((state) => state.setSelectedDateRange);
  const setScoreBoardLoading = useDataQualityReportStore((state) => state.setScoreBoardLoading);
  const setScoreBoardValue = useDataQualityReportStore((state) => state.setScoreBoardValue);
  const setSelectedScoreType = useDataQualityReportStore((state) => state.setSelectedScoreType);
  const setDataDetails = useDataQualityReportStore((state) => state.setDataDetails);
  const failedFilesType = useDataQualityReportStore((state) => state.failedFilesType);
  const setFailedFilesType = useDataQualityReportStore((state) => state.setFailedFilesType);
  const selectedScoreType = useDataQualityReportStore((state) => state.selectedScoreType);

  const [loading, setLoading] = useState<boolean>(false);
  const [payerList, setPayerList] = useState<any>([]);
  const [fileTypeList, setFileTypeList] = useState<any>([]);

  const viewTypeOptions = [
    { label: <i className="icon-list" />, value: "table" },
    { label: <i className="icon-grid" />, value: "graph" },
  ];
  const ALL_PAYOR = "All Payers";
  const ALL_FILES = "All Files";
  const defaultDateForFilters = moment(moment().format("YYYY/MM/DD"));

  const fetchScoreBoard = useCallback((payer: any, fileType: any, selectedDate: any, failedFilesType: any, scoreType:any) => {
    if (!isEmpty(payer) && !isEmpty(fileType)) {
      setScoreBoardLoading(true);
      setDataDetails([]);
      WellBeEMPIService.getScoreBoardData(payer, fileType, selectedDate?.startDate, selectedDate?.endDate, failedFilesType).subscribe({
        next: (response: any) => {
          const result: any[] = response?.data?.data?.result || [];
          if (!scoreType?.id) {
            setSelectedScoreType(size(result) >= 2 ? result[1] : null);
          }
          setScoreBoardValue(result);
          setScoreBoardLoading(false);
        },
        error: (error) => {
          console.log(error);
          setScoreBoardValue([]);
          setScoreBoardLoading(false);
        },
      });
    }
  }, []);

  useEffect(() => {
    if (payer && fileType && selectedDateRange) {
      fetchScoreBoard(payer, fileType, selectedDateRange, failedFilesType, selectedScoreType);
    }
  }, [payer, fileType, selectedDateRange]);

  const fetchPayerList = useCallback(() => {
    WellBeEMPIService.getPayersList().subscribe({
      next: (response) => {
        let tempPayerList = response.data.data.payers?.map((item: any) => item?.name);
        tempPayerList?.unshift(ALL_PAYOR);
        setPayerList(tempPayerList);
        if (tempPayerList?.length > 0) {
          setPayer([tempPayerList?.[0]]);
        }
        setLoading(false);
      },
      error: (e) => {
        console.log(e);
        setLoading(false);
      },
    });
  }, []);

  const fetchFileTypeList = useCallback(() => {
    WellBeEMPIService.getFileTypeList().subscribe({
      next: (response) => {
        let tempFileTypeList = response.data.data.fileTypes?.map((item: any) => item?.label);
        tempFileTypeList?.unshift(ALL_FILES);
        setFileTypeList(tempFileTypeList);
        if (tempFileTypeList?.length > 0) {
          setFileType([tempFileTypeList?.[0]]);
        }
        setLoading(false);
      },
      error: (e) => {
        console.log(e);
        setLoading(false);
      },
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchPayerList();
    fetchFileTypeList();
  }, []);

  const onPayorSelect = (payor: any) => {
    if (payer.includes(payor)) {
      let temp = cloneDeep(payer);
      temp = temp.filter((item: any) => item !== payor);
      if (temp.length === 0) {
        temp = [ALL_PAYOR];
      }
      setPayer(temp);
    } else {
      let temp = [...payer, payor];
      if (payor === ALL_PAYOR) {
        temp = [ALL_PAYOR];
      } else if (temp.includes(ALL_PAYOR)) {
        temp = temp.filter((item: any) => item !== ALL_PAYOR);
      }
      setPayer(temp);
    }
  };

  const onFileTypeSelect = (file: any) => {
    if (fileType.includes(file)) {
      let temp = cloneDeep(fileType);
      temp = temp.filter((item: any) => item !== file);
      if (temp.length === 0) {
        temp = [ALL_FILES];
      }
      setFileType(temp);
    } else {
      let temp = [...fileType, file];
      if (file === ALL_FILES) {
        temp = [ALL_FILES];
      } else if (temp.includes(ALL_FILES)) {
        temp = temp.filter((item: any) => item !== ALL_FILES);
      }
      setFileType(temp);
    }
  };

  return (
    <div className="filter-section">
      <div className="filter-section__item" style={{ display: "flex", alignItems: "center" }}>
        {loading ? (
          <div className="shimmerBG"></div>
        ) : (
          view === "table" && (
            <div className="filter-section__item__switch-container">
              <div className="filter-section__item__switch-container__button">
                <div className="filter-section__item__switch-container__button__label">
                  <div className={`filter-section__item__switch-container__button__label__success ${failedFilesType ? "" : "fileType__selected"}`} onClick={() => setFailedFilesType(false)}>
                    Successful Files
                  </div>
                  <div className={`filter-section__item__switch-container__button__label__failed ${failedFilesType ? "fileType__selected" : ""}`} onClick={() => setFailedFilesType(true)}>
                    Failed Files
                  </div>
                </div>
                {/* <div style={{ background: `${!failedFilesType ? "red" : "green"}` }} className="filter-section__item__switch-container__button__indicator" /> */}
              </div>
              {/* <div className="filter-section__item__switch-container__switch"><Switch checked={failedFilesType} onChange={(value)=>setFailedFilesType(value)}/></div> */}
            </div>
          )
        )}
      </div>
      <div className="filter-section__item">
        {loading ? (
          <div className="shimmerBG"></div>
        ) : (
          <Select
            value={`${payer.length > 1 ? `${payer?.[0]}, +${payer.length - 1}` : payer?.[0]}`}
            allowClear
            onClear={() => setPayer([ALL_PAYOR])}
            placeholder={"Select Payer"}
            style={{ width: 170 }}
            className="filter-section__item__payer"
          >
            {payerList.map((option: any, index: any) => (
              <Option key={option + index} value={option}>
                <Checkbox
                  onChange={(e: any) => {
                    onPayorSelect(option);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  checked={payer.includes(option)}
                >
                  {option}
                </Checkbox>
              </Option>
            ))}
          </Select>
        )}
      </div>
      <div className="filter-section__item">
        {loading ? (
          <div className="shimmerBG"></div>
        ) : (
          <Select
            value={`${fileType.length > 1 ? `${fileType?.[0]}, +${fileType.length - 1}` : fileType?.[0]}`}
            placeholder={"Select File Type"}
            // onChange={(value) => {
            //   setFileType(value);
            // }}
            style={{ width: 170 }}
            className="filter-section__item__file-type"
            allowClear
            onClear={() => setFileType([ALL_FILES])}
          >
            {fileTypeList.map((option: any, index: any) => (
              <Option key={option + index} value={option}>
                <Checkbox
                  onChange={(e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onFileTypeSelect(option);
                  }}
                  checked={fileType.includes(option)}
                >
                  {option}
                </Checkbox>
              </Option>
            ))}
          </Select>
        )}
      </div>
      <div className="filter-section__item">
        <Radio.Group options={viewTypeOptions} onChange={(e) => onViewChange(e.target.value)} value={view} optionType="button" className="filter-section__item__view" size="large" />
      </div>
      {!disableDateRange && (
        <div className="filter-section__item">
          <RangePicker
            value={selectedDateRange ? [moment(selectedDateRange.startDate), moment(selectedDateRange.endDate)] : null}
            onChange={(dates) => {
              const formattedStartDate = dates?.[0] ? momentTZ(dates?.[0]).utc().tz("Asia/Kolkata").format("YYYY-MM-DD") : null; // use helper function to get Timezone
              const formattedEndDate = dates?.[1] ? momentTZ(dates?.[1]).utc().tz("Asia/Kolkata").format("YYYY-MM-DD") : null; // use helper function to get Timezone
              setSelectedDateRange({ startDate: formattedStartDate, endDate: formattedEndDate });
            }}
            format="YYYY-MM-DD"
            disabled={view !== "table"} // need to disable this for graph view
            defaultValue={[defaultDateForFilters, defaultDateForFilters]}
            allowClear={false}
            disabledDate={(current: any) => {
              return current > moment();
            }}
            dropdownClassName="filter-section__item__date-range-picker"
            ranges={{
              "More Filters:": [null, null],
              "Current Month to Date": [moment().startOf("month"), moment()],
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Filters;
